import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { usePublicSettings } from '../../hooks/usePublicSettings'
import { AuthLayout, ErrorNote, InfoNote, SubmitButton, INPUT, SECONDARY_BUTTON, LINK, whatsappLink } from './AuthLayout'
import SetPasswordForm from './SetPasswordForm'

/**
 * Sign in with a one-time code sent on WhatsApp: phone → code → signed in → optional "Set a password".
 * For customers who only ever used WhatsApp (no web password), who forgot their password, or whose
 * sign-up found their number already has an account. The number can be typed in any form
 * ("70 123 456", "+961 70 123 456"). WhatsApp only lets us write to people who wrote to us recently:
 * if the number can't be reached, the screen says to message us first (business number from settings).
 */
export default function WhatsAppSignIn() {
  const { requestWhatsAppCode, verifyWhatsAppCode, user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const settings = usePublicSettings()
  const brand = settings?.['onboarding.brand_name']
  const businessNumber = settings?.['growth.whatsapp_number']
  const cc = settings?.['market.phone_country_code']

  const [step, setStep] = useState('phone')          // phone → code → password
  const [phone, setPhone] = useState(location.state?.phone || '')
  const [code, setCode] = useState('')
  const [info, setInfo] = useState(null)
  const [unreachable, setUnreachable] = useState(null)   // { message, whatsappNumber }
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [account, setAccount] = useState(null)       // { hasPassword, hasEmail, setPasswordMinutes }
  const signedInHere = useRef(false)

  // Already signed in when the screen opened → home (not after signing in here: the password step follows)
  useEffect(() => {
    if (user && !signedInHere.current) navigate('/', { replace: true })
  }, [user, navigate])

  const askForCode = async (e) => {
    e?.preventDefault()
    setError(null); setInfo(null); setUnreachable(null)
    setLoading(true)
    const r = await requestWhatsAppCode(phone.trim())
    setLoading(false)
    if (!r.success) { setError(r.error); return }
    if (r.reachable === false) { setUnreachable({ message: r.message, whatsappNumber: r.whatsappNumber || businessNumber }); return }
    setInfo(r.message)
    setCode('')
    setStep('code')
  }

  const checkCode = async (e) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    signedInHere.current = true
    const r = await verifyWhatsAppCode(phone.trim(), code.trim())
    setLoading(false)
    if (!r.success) { signedInHere.current = false; setError(r.error); return }
    setAccount(r)
    setStep('password')
  }

  if (step === 'password') {
    return (
      <AuthLayout title="You're signed in"
        subtitle={account?.hasPassword ? 'Forgot your password? Choose a new one here (optional)' : 'Set a password to also sign in with your email (optional)'}>
        <SetPasswordForm needsEmail={!account?.hasEmail} onDone={() => navigate('/', { replace: true })} />
        <button type="button" className={`${SECONDARY_BUTTON} mt-3`} onClick={() => navigate('/', { replace: true })}>
          Not now
        </button>
        <p className="text-center text-xs text-slate-500 mt-4">
          You can always sign in again with a WhatsApp code.
        </p>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Sign in with WhatsApp" subtitle={step === 'phone' ? `We'll send a code to your WhatsApp` : 'Enter the code we sent on WhatsApp'}>
      <ErrorNote>{error}</ErrorNote>
      {unreachable && (
        <div role="alert" className="mb-4 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
          <p>{unreachable.message}</p>
          {unreachable.whatsappNumber && (
            <a href={whatsappLink(unreachable.whatsappNumber)} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 font-medium underline">
              Message {brand || 'us'} on WhatsApp
            </a>
          )}
        </div>
      )}

      {step === 'phone' ? (
        <form onSubmit={askForCode} className="space-y-4" aria-label="Get a code on WhatsApp">
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-1">WhatsApp number</label>
            <input id="phone" type="tel" inputMode="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
              className={INPUT} placeholder={cc ? `+${cc} …` : 'Your WhatsApp number'} required />
          </div>
          <SubmitButton loading={loading} busyText="Sending…">Get a code on WhatsApp</SubmitButton>
        </form>
      ) : (
        <>
          {!error && <InfoNote>{info}</InfoNote>}
          <form onSubmit={checkCode} className="space-y-4" aria-label="Enter the code">
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-slate-700 mb-1">Code</label>
              <input id="code" type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className={`${INPUT} tracking-[0.4em] text-center text-lg`} placeholder="••••••" required />
            </div>
            <SubmitButton loading={loading} busyText="Checking…">Sign in</SubmitButton>
          </form>
          <div className="mt-4 flex flex-col gap-2 text-center text-sm">
            <button type="button" className={LINK} onClick={askForCode} disabled={loading}>Send a new code</button>
            <button type="button" className={LINK} onClick={() => { setStep('phone'); setError(null); setInfo(null) }}>Use another number</button>
          </div>
        </>
      )}

      {businessNumber && step === 'code' && (
        <p className="text-center text-xs text-slate-500 mt-4">
          No code after a minute? Send any message to{' '}
          <a href={whatsappLink(businessNumber)} target="_blank" rel="noopener noreferrer" className="underline text-slate-700">{brand || 'us'} on WhatsApp</a>
          , then ask for a new code.
        </p>
      )}
      <div className="mt-6 space-y-2 text-center text-sm">
        <p><Link to="/login" className={LINK}>Sign in with email and password</Link></p>
      </div>
    </AuthLayout>
  )
}
